import { Injectable } from '@angular/core';
import { AuthenticationService, ClientService, ContentService } from '@edassist/api';
import { retry, switchMap, tap } from 'rxjs/operators';
import { AppConfigService, constants, LocalStorageService, SessionStorageService } from '@edassist/common';
import { catchError, forkJoin, Observable, of, take, map } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { Auth0TokenService } from '../auth0-token/auth0-token.service';

@Injectable({ providedIn: 'root' })
export class StartupConfigService {
	constructor(
		private readonly contentService: ContentService,
		private readonly auth0Service: AuthService,
		private readonly authService: AuthenticationService,
		private readonly clientService: ClientService,
		private readonly auth0TokenService: Auth0TokenService
	) {}

	initApp(): Observable<any> {
		return this.contentService.getLoginContent({ textOnly: true }).pipe(
			retry(3),
			switchMap((content) => {
				const clientLogo = content.generalContentList.find(
					(item) => item.name === constants.contentConstants.CONTENT_NAMES.WEB_LOGO
				)?.filePath;

				const existingData = LocalStorageService.getItem(constants.localStorageKeys.CLIENT_DETAILS) ?? {};
				LocalStorageService.setItem(constants.localStorageKeys.CLIENT_DETAILS, {
					...existingData,
					...content.clientLoginDetails,
					id: content.clientLoginDetails.clientId,
					name: content.clientLoginDetails.clientName,
					clientLogo: clientLogo,
					ssoEnabled: content.clientLoginDetails.ssoEnabled,
					clientLoginDetails: content.clientLoginDetails,
					clientLogoPath: AppConfigService.getSettings().endPoints.cdn + clientLogo,
					clientFeaturesReferences: content.clientLoginDetails.clientFeaturesReferences,
					isCareerPathwaysEnabled: content.clientLoginDetails.careerPathwaysEnabled
				});

				SessionStorageService.setItem(constants.sessionStorageKeys.CHAT_LINGUAL_INITIALIZED, false);
				SessionStorageService.setItem(constants.sessionStorageKeys.WEB_ANALYTICS_INITIALIZED, false);
				SessionStorageService.setItem(constants.sessionStorageKeys.ONE_TRUST_ACTIVE_GROUPS, undefined);

				return this.handleAuth0Authentication();
			}),
			map(() => true),
			catchError(() => of(false))
		);
	}

	private handleAuth0Authentication(): Observable<any> {
		return this.auth0Service.isAuthenticated$.pipe(
			take(1),
			switchMap((isAuthenticated) => {
				if (!isAuthenticated) {
					return of(false);
				}

				return this.getAuth0Tokens();
			})
		);
	}

	private getAuth0Tokens(): Observable<any> {
		return forkJoin({
			idTokenClaims: this.auth0Service.idTokenClaims$.pipe(take(1)),
			accessToken: this.auth0Service.getAccessTokenSilently()
		}).pipe(
			switchMap(({ idTokenClaims, accessToken }) => {
				if (!idTokenClaims || !accessToken) {
					return of(false);
				}

				console.log('User is authenticated with Auth0');
				this.auth0TokenService.accessToken = accessToken;

				return this.handlePostAuth0Login();
			}),
			catchError((error) => {
				console.error('Error getting Auth0 tokens:', error);

				return of(false);
			})
		);
	}

	private handlePostAuth0Login(): Observable<any> {
		return this.authService.postAuth0Login().pipe(
			switchMap((response) => this.handleProductTypes(response)),
			catchError(() => {
				console.error('Failed to get user details from SSO');

				return of(false);
			})
		);
	}

	private handleProductTypes(response: any): Observable<any> {
		const participantClientId = response?.clientFeatures?.id;
		const currentClientId = LocalStorageService.getItem(constants.localStorageKeys.CLIENT_DETAILS)?.clientId;
		if (participantClientId !== currentClientId) {
			this.auth0Service.logout({
				logoutParams: {
					returnTo: window.location.origin + constants.routes.LOGIN_UI.ROOT,
				},
			});

			return of(false);
		}

		return this.clientService.getProductTypes(response.clientFeatures.id, response.userDetails.participantId).pipe(
			tap(() => {
				this.authService.isAuth0Authenticated = true;
			}),
			map((productTypes) => productTypes)
		);
	}
}
